import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Store, ChevronDown } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { useEditorStore } from '../stores/editorStore'
import { fetchAvailableStores } from '../services/api'
import { hasStoreChoice, isMultiStoreList } from '../utils/storeAccess'

/**
 * Select de troca de loja no Header, sem relogin.
 * Visível para perfis multistore (todas as lojas) e multi-loja (lista do perfil).
 *
 * IMPORTANTE: este componente apenas NAVEGA para a URL da nova loja.
 * Quem executa a troca de fato (switchStore) é o StoreRouteGuard, dono único
 * desse fluxo — ter dois pontos chamando switchStore causava troca em loop
 * (o guard via URL antiga ≠ loja nova e revertia a troca em andamento).
 */
export default function StoreSwitcher() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { isDirty } = useEditorStore()
  const [stores, setStores] = useState<string[]>([])

  // Fallback para sessões persistidas antes do deploy (sem allowedStores):
  // multistore antigo continua com acesso total.
  const allowedStores = user?.allowedStores?.length
    ? user.allowedStores
    : user?.isMultiStore
      ? ['multistore']
      : []
  const canSwitch = hasStoreChoice(allowedStores)

  useEffect(() => {
    if (!canSwitch) return
    let cancelled = false
    fetchAvailableStores().then((all) => {
      if (cancelled) return
      const active = all.map((s) => s.storename.toLowerCase())
      const visible = isMultiStoreList(allowedStores)
        ? active
        : active.filter((s) => allowedStores.includes(s))
      setStores(visible)
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSwitch, allowedStores.join(',')])

  if (!canSwitch || !user) return null

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const target = e.target.value
    if (!target || target === user.storeName) return

    if (isDirty) {
      const confirmed = window.confirm(
        'Você tem alterações não salvas. Trocar de loja irá descartá-las. Deseja continuar?'
      )
      if (!confirmed) {
        // Restaura o valor do select para a loja atual
        e.target.value = user.storeName
        return
      }
    }

    // Apenas navega — o StoreRouteGuard detecta URL ≠ loja ativa e executa a troca
    navigate(`/editor/${target}/home`)
  }

  return (
    <div className="relative flex items-center">
      <div className="absolute left-2 pointer-events-none">
        <Store className="w-4 h-4 text-gray-400" />
      </div>
      <select
        value={user.storeName}
        onChange={handleChange}
        title="Trocar de loja"
        className="appearance-none pl-8 pr-7 py-1.5 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60 cursor-pointer"
      >
        {/* Garante que a loja atual apareça mesmo antes da lista carregar */}
        {!stores.includes(user.storeName) && (
          <option value={user.storeName}>{user.storeName}</option>
        )}
        {stores.map((store) => (
          <option key={store} value={store}>
            {store}
          </option>
        ))}
      </select>
      <div className="absolute right-2 pointer-events-none">
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </div>
    </div>
  )
}
